import React from 'react';
import PropTypes from 'prop-types';
import { Drawer, Button } from '@unite-us/app-components';
import { useTaskSendOrganizations } from './tasksQueries';

const SyncScreeningTasksModal = ({
  show,
  onClose,
  onProcessScreenings,
  onProcessOverdueTasks,
  processingJobType,
  currentProviderId,
  leadSCNFlag,
}) => {
  const isProcessingScreenings = processingJobType === 'screenings_task';
  const isProcessingOverdueTasks = processingJobType === 'task_overdue';
  const isProcessing = !!processingJobType;
  const {
    orgOptions,
    isLoading: loadingTaskSendOrganizations,
    isScnLeadProvider,
  } = useTaskSendOrganizations(currentProviderId, leadSCNFlag);
  let providerScopeMessage;
  if (isScnLeadProvider) {
    providerScopeMessage = [
      'You are logged in as an SCN lead provider with',
      `${orgOptions.length} available network organization option${orgOptions.length === 1 ? '' : 's'}.`,
    ].join(' ');
  } else {
    providerScopeMessage = [
      'You are logged in as a non-lead provider with',
      `${orgOptions.length} available lead organization option${orgOptions.length === 1 ? '' : 's'}.`,
    ].join(' ');
  }

  return (
    <Drawer
      open={show}
      onClose={onClose}
      secondary
      width="500px"
      topOffset="60px"
      className="border border-solid border-medium-border-blue"
      id="tasks-v3-sync-screenings-flyout"
    >
      <div className="h-full p-6 bg-light-fill-grey">
        <div className="pb-6">
          <div>
            <h3 className="font-semibold leading-7">Internal Use</h3>
            <p className="mt-1 text-sm text-gray-600">
              Internal-use controls for background jobs that normally run automatically throughout
              the day.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded border border-solid border-medium-border-blue bg-white p-4">
            <h4 className="font-semibold text-dark-blue">Process Screenings</h4>
            <div className="mt-2 space-y-2 text-sm text-gray-700">
              <p>
                Process completed screenings from the last 24 hours for screening types `self` and
                `external`, and create tasks when an associated task does not already exist.
              </p>
              <p>
                For `Public Health Solutions`, a screening must include at least 1 identified need
                to create a task. For all other providers, tasks can still be created even when no
                identified needs are present.
              </p>
              <p>
                This job is intended to run automatically 2-3 times per day. In production, users
                will not be able to trigger it manually.
              </p>
            </div>
            <Button
              label={isProcessingScreenings ? 'Processing Screenings...' : 'Process Screenings'}
              data-testid="process-screenings-button"
              primary
              disabled={isProcessing}
              onClick={onProcessScreenings}
              className="mt-4 font-regular-font"
              icon={isProcessingScreenings ? undefined : { name: 'Refresh', color: 'white', position: 'left' }}
            />
          </div>

          <div className="rounded border border-solid border-medium-border-blue bg-white p-4">
            <h4 className="font-semibold text-dark-blue">Process Overdue Tasks</h4>
            <div className="mt-2 space-y-2 text-sm text-gray-700">
              <p>
                Run the overdue task processor to update tasks that should move into an overdue
                state.
              </p>
              <p>
                This job is intended to run automatically 2-3 times per day. In production, users
                will not be able to trigger it manually.
              </p>
            </div>
            <Button
              label={isProcessingOverdueTasks ? 'Processing Overdue Tasks...' : 'Process Overdue Tasks'}
              data-testid="process-overdue-tasks-button"
              primary
              disabled={isProcessing}
              onClick={onProcessOverdueTasks}
              className="mt-4 font-regular-font"
              icon={isProcessingOverdueTasks ? undefined : { name: 'Refresh', color: 'white', position: 'left' }}
            />
          </div>

          <div className="rounded border border-solid border-medium-border-blue bg-white p-4">
            <h4 className="font-semibold text-dark-blue">General Assignments</h4>
            <div className="mt-2 space-y-2 text-sm text-gray-700">
              <p>User-created tasks cannot be assigned to other providers.</p>
              <p>You can assign tasks to anyone in your organization.</p>
            </div>
          </div>

          <div className="rounded border border-solid border-medium-border-blue bg-white p-4">
            <h4 className="font-semibold text-dark-blue">Screening Tasks Assignments</h4>
            <div className="mt-2 space-y-2 text-sm text-gray-700">
              <p>
                If you are part of a Customer Initiative Provider group and you are a lead
                provider, you can assign tasks to any provider inside that Customer Initiative
                Provider group.
              </p>
              <p>
                If you are part of a Customer Initiative Provider group and you are a non-lead
                provider, you can only assign tasks to the lead provider of the Customer
                Initiative Provider groups you are in.
              </p>
            </div>
          </div>

          <div className="rounded border border-solid border-medium-border-blue bg-white p-4">
            <h4 className="font-semibold text-dark-blue">Current Provider Scope</h4>
            <div className="mt-2 space-y-3 text-sm text-gray-700">
              {loadingTaskSendOrganizations ? (
                <p>Loading assignment scope...</p>
              ) : (
                <>
                  <p>
                    {providerScopeMessage}
                  </p>

                  {orgOptions.length > 0 ? (
                    <div>
                      <p className="font-semibold text-dark-blue">Organizations you can send to:</p>
                      <ul className="mt-2 list-disc pl-5 space-y-1">
                        {orgOptions.map((orgOption) => (
                          <li key={orgOption.value}>{orgOption.label}</li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <p>No organizations are currently available for reassignment.</p>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="pb-8">
            <Button
              label="Close"
              data-testid="close-process-tasks-flyout"
              secondary
              disabled={isProcessing}
              onClick={onClose}
              className="font-regular-font"
            />
          </div>
        </div>
      </div>
    </Drawer>
  );
};

SyncScreeningTasksModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onProcessScreenings: PropTypes.func.isRequired,
  onProcessOverdueTasks: PropTypes.func.isRequired,
  processingJobType: PropTypes.string,
  currentProviderId: PropTypes.string.isRequired,
  leadSCNFlag: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
};

SyncScreeningTasksModal.defaultProps = {
  processingJobType: null,
  leadSCNFlag: null,
};

export default SyncScreeningTasksModal;
